import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

const FULL_TEXT = "Alert me when any client hasn't responded in 7 days";

const TICKER_TOOLS = [
  { text: "Track response rates", color: COLORS.neonPink },
  { text: "Weekly summaries", color: COLORS.neonBlue },
  { text: "Meeting prep auto", color: COLORS.neonGreen },
  { text: "Patient reminders", color: COLORS.neonYellow },
  { text: "Email analytics", color: COLORS.neonPurple },
  { text: "Sentiment alerts", color: COLORS.neonOrange },
];

// Absolute pixel coordinates for arrow endpoints (1920x1080 canvas)
// Left panel speech bubble: x=80, y=250, w=400, h=120 → right edge center = (480, 310)
// Center compiler: x=750, y=230, w=280, h=200 → left edge center = (750, 330), right edge center = (1030, 330)
// Right panel cards: x=1250, y=180/290/400, w=360, h=80 → left edge center

const ARROW_USER_TO_COMPILER = {
  x1: 480, y1: 310,
  x2: 750, y2: 330,
};

const OUTPUT_ARROWS = [
  { x1: 1030, y1: 280, x2: 1250, y2: 220, color: COLORS.neonBlue },
  { x1: 1030, y1: 330, x2: 1250, y2: 330, color: COLORS.neonGreen },
  { x1: 1030, y1: 380, x2: 1250, y2: 440, color: COLORS.neonOrange },
];

const VARIATION_CARDS = [
  { icon: "📧", label: "Email Alert", color: COLORS.neonBlue, delay: 160, y: 180 },
  { icon: "💬", label: "WhatsApp Ping", color: COLORS.neonGreen, delay: 185, y: 290, selected: true },
  { icon: "🔔", label: "Dashboard Badge", color: COLORS.neonOrange, delay: 210, y: 400 },
];

// Small floating background particles
const BG_PARTICLES = [
  { x: 160,  y: 200, r: 3, speed: 0.02, phase: 0.0  },
  { x: 580,  y: 520, r: 2, speed: 0.03, phase: 1.2  },
  { x: 960,  y: 150, r: 4, speed: 0.015,phase: 2.4 },
  { x: 1340, y: 600, r: 3, speed: 0.025,phase: 0.8 },
  { x: 1700, y: 300, r: 2, speed: 0.035,phase: 3.1 },
  { x: 420,  y: 800, r: 3, speed: 0.02, phase: 1.7  },
];

// Flow particles along output arrows (2 per arrow)
const FLOW_PARTICLES = OUTPUT_ARROWS.flatMap((arrow, ai) => [
  { arrowIndex: ai, offset: 0.0, color: arrow.color },
  { arrowIndex: ai, offset: 0.5, color: arrow.color },
]);

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export const Scene08Phase3: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // ── Exit fade ──────────────────────────────────────────
  const exitOpacity = interpolate(
    frame,
    [durationInFrames - 30, durationInFrames],
    [1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // ── Title spring ───────────────────────────────────────
  const titleSpring = spring({ frame, fps, config: SPRING.snappy });
  const titleY = interpolate(titleSpring, [0, 1], [-24, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const titleOpacity = interpolate(titleSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // ── Typing animation ───────────────────────────────────
  const typingStart = 30;
  const typingEnd = 120;
  const charsToShow = Math.floor(
    interpolate(frame, [typingStart, typingEnd], [0, FULL_TEXT.length], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    })
  );
  const displayedText = FULL_TEXT.slice(0, charsToShow);
  const showCursor = frame >= typingStart && frame < typingEnd + 24;
  const cursorBlink = Math.floor(frame / 8) % 2 === 0;

  // ── Left bubble spring ─────────────────────────────────
  const bubbleSpring = spring({ frame: frame - 10, fps, config: SPRING.bouncy });
  const bubbleScale = interpolate(bubbleSpring, [0, 1], [0.85, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const bubbleOpacity = interpolate(bubbleSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // ── Compiler spring ────────────────────────────────────
  const compilerSpring = spring({ frame: frame - 100, fps, config: SPRING.bouncy });
  const compilerScale = interpolate(compilerSpring, [0, 1], [0.7, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const compilerOpacity = interpolate(compilerSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // ── Arrow opacity (appears with compiler) ──────────────
  const arrowOpacity = interpolate(compilerSpring, [0, 1], [0, 0.85], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // ── Gear rotation ──────────────────────────────────────
  const gearRotation = frame * 2;

  // ── WhatsApp selection pulse at frame 260 ──────────────
  const selectionPulse = interpolate(
    frame,
    [260, 275, 290, 310],
    [0, 1, 0.6, 0.8],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // ── Dashed arrow animation ─────────────────────────────
  const arrowDash = -frame * 2;

  // ── Ticker scroll ──────────────────────────────────────
  const PILL_W = 200;
  const GAP = 16;
  const TOTAL_W = TICKER_TOOLS.length * (PILL_W + GAP);
  const tickerOffset = -(frame * 0.8) % TOTAL_W;

  // ── Parallax background shifts ─────────────────────────
  const gridShiftX = Math.sin(frame * 0.008) * 6;

  // ── Orb drifts ─────────────────────────────────────────
  const orb1X = 300 + Math.sin(frame * 0.006) * 80;
  const orb1Y = 400 + Math.cos(frame * 0.004) * 60;
  const orb2X = 1600 + Math.cos(frame * 0.005) * 100;
  const orb2Y = 250 + Math.sin(frame * 0.007) * 70;
  const orb3X = 960 + Math.sin(frame * 0.009) * 60;
  const orb3Y = 700 + Math.cos(frame * 0.006) * 50;

  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        opacity: exitOpacity,
        overflow: "hidden",
      }}
    >
      {/* ── Parallax grid ─────────────────────────────── */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0.04,
          transform: `translateX(${gridShiftX}px)`,
          pointerEvents: "none",
        }}
      >
        <defs>
          <pattern id="grid08" width="60" height="60" patternUnits="userSpaceOnUse">
            <path
              d="M 60 0 L 0 0 0 60"
              fill="none"
              stroke={COLORS.neonOrange}
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid08)" />
      </svg>

      {/* ── Background orbs ───────────────────────────── */}
      <div
        style={{
          position: "absolute",
          left: orb1X - 300,
          top: orb1Y - 300,
          width: 600,
          height: 600,
          background: `radial-gradient(circle, ${COLORS.neonPurple}12 0%, transparent 70%)`,
          borderRadius: "50%",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: orb2X - 280,
          top: orb2Y - 280,
          width: 560,
          height: 560,
          background: `radial-gradient(circle, ${COLORS.neonOrange}10 0%, transparent 70%)`,
          borderRadius: "50%",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: orb3X - 250,
          top: orb3Y - 250,
          width: 500,
          height: 500,
          background: `radial-gradient(circle, ${COLORS.neonBlue}10 0%, transparent 70%)`,
          borderRadius: "50%",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />

      {/* ── Small floating background particles ───────── */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {BG_PARTICLES.map((p, i) => {
          const px = p.x + Math.sin(frame * p.speed + p.phase) * 20;
          const py = p.y + Math.cos(frame * p.speed * 0.7 + p.phase) * 14;
          const colors = [
            COLORS.neonPurple,
            COLORS.neonBlue,
            COLORS.neonGreen,
            COLORS.neonOrange,
            COLORS.neonPink,
            COLORS.neonYellow,
          ];
          const c = colors[i % colors.length];
          return (
            <circle
              key={i}
              cx={px}
              cy={py}
              r={p.r}
              fill={c}
              opacity={0.35}
              style={{ filter: `drop-shadow(0 0 4px ${c})` }}
            />
          );
        })}
      </svg>

      {/* ── Title ─────────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          top: 28,
          left: 0,
          right: 0,
          textAlign: "center",
          transform: `translateY(${titleY}px)`,
          opacity: titleOpacity,
        }}
      >
        <div
          style={{
            fontSize: 44,
            fontFamily: FONTS.heading,
            fontWeight: 800,
            color: COLORS.neonOrange,
            textShadow: `0 0 30px ${COLORS.neonOrange}80, 0 0 60px ${COLORS.neonOrange}30`,
            letterSpacing: -0.5,
          }}
        >
          Phase 3: User-Defined Tools
        </div>
        <div
          style={{
            fontSize: 18,
            fontFamily: FONTS.body,
            fontWeight: 600,
            color: COLORS.neonYellow,
            marginTop: 6,
            textShadow: `0 0 15px ${COLORS.neonYellow}60`,
          }}
        >
          The Infinite Layer
        </div>
      </div>

      {/* ═══════════════════════════════════════════════
          MAIN 3-PANEL LAYOUT — absolute pixel positions
          on a 1920×1080 canvas
          ─────────────────────────────────────────────
          LEFT   bubble : x=80,  y=250, w=400, h=120
          CENTER compiler: x=750, y=230, w=280, h=200
          RIGHT  cards  : x=1250, y=180/290/400, w=360, h=80
      ════════════════════════════════════════════════ */}

      {/* ── LEFT: User speech bubble ──────────────────── */}
      <div
        style={{
          position: "absolute",
          left: 80,
          top: 250,
          width: 400,
          opacity: bubbleOpacity,
          transform: `scale(${bubbleScale}) perspective(800px) rotateY(3deg)`,
          transformOrigin: "left center",
        } as React.CSSProperties}
      >
        {/* Bubble card */}
        <div
          style={{
            width: 400,
            height: 120,
            boxSizing: "border-box",
            padding: "22px 24px",
            borderRadius: 20,
            borderBottomLeftRadius: 4,
            background: `linear-gradient(135deg, ${COLORS.bgCardLight}, ${COLORS.bgCard})`,
            border: `1.5px solid ${COLORS.neonPurple}50`,
            boxShadow: `0 0 24px ${COLORS.neonPurple}25, inset 0 1px 0 rgba(255,255,255,0.05)`,
            position: "relative",
          }}
        >
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 15,
              color: COLORS.textPrimary,
              lineHeight: 1.55,
              minHeight: 44,
            }}
          >
            {displayedText}
            {showCursor && cursorBlink && (
              <span
                style={{
                  display: "inline-block",
                  width: 2,
                  height: "1em",
                  background: COLORS.neonGreen,
                  marginLeft: 2,
                  verticalAlign: "text-bottom",
                  boxShadow: `0 0 6px ${COLORS.neonGreen}`,
                }}
              />
            )}
          </div>

          {/* Bubble tail */}
          <div
            style={{
              position: "absolute",
              bottom: -12,
              left: 20,
              width: 0,
              height: 0,
              borderLeft: "12px solid transparent",
              borderRight: "0px solid transparent",
              borderTop: `12px solid ${COLORS.bgCardLight}`,
            }}
          />
        </div>

        {/* Label below bubble */}
        <div
          style={{
            marginTop: 18,
            fontFamily: FONTS.body,
            fontSize: 12,
            color: COLORS.textMuted,
            paddingLeft: 8,
            letterSpacing: 0.3,
          }}
        >
          User describes in plain English
        </div>
      </div>

      {/* ── CENTER: Tool Compiler ─────────────────────── */}
      <div
        style={{
          position: "absolute",
          left: 750,
          top: 230,
          width: 280,
          height: 200,
          boxSizing: "border-box",
          padding: "28px 24px",
          borderRadius: 18,
          background: `linear-gradient(135deg, ${COLORS.neonPurple}22, ${COLORS.neonPurple}0C)`,
          border: `2px solid ${COLORS.neonPurple}`,
          boxShadow: `0 0 36px ${COLORS.neonPurple}55, 0 0 70px ${COLORS.neonPurple}20, inset 0 1px 0 rgba(255,255,255,0.06)`,
          textAlign: "center",
          opacity: compilerOpacity,
          transform: `scale(${compilerScale}) perspective(800px) rotateY(-3deg)`,
          transformOrigin: "center center",
        } as React.CSSProperties}
      >
        <div
          style={{
            fontSize: 40,
            display: "inline-block",
            transform: `rotate(${gearRotation}deg)`,
            transformOrigin: "center",
            lineHeight: 1,
          }}
        >
          ⚙️
        </div>
        <div
          style={{
            fontFamily: FONTS.heading,
            fontSize: 18,
            fontWeight: 700,
            color: COLORS.neonPurple,
            marginTop: 10,
            textShadow: `0 0 14px ${COLORS.neonPurple}80`,
          }}
        >
          Tool Compiler
        </div>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 12,
            color: COLORS.textMuted,
            marginTop: 5,
          }}
        >
          Generates 3 variations
        </div>
      </div>

      {/* ── RIGHT: Variation cards ────────────────────── */}
      {VARIATION_CARDS.map((card, i) => {
        const cardSpring = spring({
          frame: frame - card.delay,
          fps,
          config: SPRING.bouncy,
        });
        const cardOpacity = interpolate(cardSpring, [0, 1], [0, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        const cardTranslateX = interpolate(cardSpring, [0, 1], [60, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });

        const isSelected = card.selected === true;
        const pulseScale = isSelected
          ? 1 +
            interpolate(selectionPulse, [0, 1], [0, 0.05], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            })
          : 1;
        const glowExtra = isSelected ? selectionPulse : 0;

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: 1250,
              top: card.y,
              width: 360,
              height: 80,
              boxSizing: "border-box",
              padding: "0 22px",
              borderRadius: 14,
              background: `linear-gradient(135deg, ${card.color}${isSelected ? "28" : "16"}, ${card.color}08)`,
              border: `${isSelected ? "2.5" : "1.5"}px solid ${card.color}${isSelected ? "" : "55"}`,
              display: "flex",
              alignItems: "center",
              gap: 16,
              opacity: cardOpacity,
              transform: `translateX(${cardTranslateX}px) scale(${pulseScale}) perspective(800px) rotateY(-2deg)`,
              transformOrigin: "left center",
              boxShadow: `0 0 ${20 + glowExtra * 32}px ${card.color}${isSelected ? "65" : "28"}, 0 0 ${40 + glowExtra * 40}px ${card.color}18`,
            } as React.CSSProperties}
          >
            <span style={{ fontSize: 28, flexShrink: 0 }}>{card.icon}</span>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontFamily: FONTS.heading,
                  fontSize: 16,
                  fontWeight: 700,
                  color: card.color,
                  textShadow: `0 0 10px ${card.color}60`,
                }}
              >
                {card.label}
              </div>
              {isSelected && (
                <div
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 11,
                    color: COLORS.neonGreen,
                    marginTop: 3,
                    fontWeight: 600,
                    textShadow: `0 0 8px ${COLORS.neonGreen}60`,
                  }}
                >
                  ✓ Selected
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* ══════════════════════════════════════════════════
          SVG OVERLAY — full 1920×1080, all arrows in one SVG
          Coordinates are absolute canvas pixels.
      ═════════════════════════════════════════════════ */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 1920,
          height: 1080,
          pointerEvents: "none",
          opacity: arrowOpacity,
        }}
      >
        <defs>
          {/* Arrowhead markers */}
          <marker
            id="arrow-purple"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill={COLORS.neonPurple} opacity={0.9} />
          </marker>
          <marker
            id="arrow-blue"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill={COLORS.neonBlue} opacity={0.9} />
          </marker>
          <marker
            id="arrow-green"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill={COLORS.neonGreen} opacity={0.9} />
          </marker>
          <marker
            id="arrow-orange"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill={COLORS.neonOrange} opacity={0.9} />
          </marker>

          {/* Gradient for user → compiler arrow */}
          <linearGradient id="grad-purple" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLORS.neonPurple} stopOpacity={0} />
            <stop offset="100%" stopColor={COLORS.neonPurple} stopOpacity={1} />
          </linearGradient>
          <linearGradient id="grad-blue" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLORS.neonBlue} stopOpacity={0} />
            <stop offset="100%" stopColor={COLORS.neonBlue} stopOpacity={1} />
          </linearGradient>
          <linearGradient id="grad-green" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLORS.neonGreen} stopOpacity={0} />
            <stop offset="100%" stopColor={COLORS.neonGreen} stopOpacity={1} />
          </linearGradient>
          <linearGradient id="grad-orange" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLORS.neonOrange} stopOpacity={0} />
            <stop offset="100%" stopColor={COLORS.neonOrange} stopOpacity={1} />
          </linearGradient>
        </defs>

        {/* Arrow 1: User bubble → Tool Compiler (curved) */}
        {/* From (480, 310) to (750, 330) — quadratic bezier curving upward */}
        <path
          d={`M ${ARROW_USER_TO_COMPILER.x1} ${ARROW_USER_TO_COMPILER.y1} Q 615 260 ${ARROW_USER_TO_COMPILER.x2} ${ARROW_USER_TO_COMPILER.y2}`}
          fill="none"
          stroke="url(#grad-purple)"
          strokeWidth="2.5"
          strokeDasharray="10 6"
          strokeDashoffset={arrowDash}
          markerEnd="url(#arrow-purple)"
        />

        {/* Arrow 2: Compiler → Email Alert card */}
        <line
          x1={OUTPUT_ARROWS[0].x1}
          y1={OUTPUT_ARROWS[0].y1}
          x2={OUTPUT_ARROWS[0].x2}
          y2={OUTPUT_ARROWS[0].y2}
          stroke="url(#grad-blue)"
          strokeWidth="2"
          strokeDasharray="9 5"
          strokeDashoffset={arrowDash}
          markerEnd="url(#arrow-blue)"
        />

        {/* Arrow 3: Compiler → WhatsApp Ping card */}
        <line
          x1={OUTPUT_ARROWS[1].x1}
          y1={OUTPUT_ARROWS[1].y1}
          x2={OUTPUT_ARROWS[1].x2}
          y2={OUTPUT_ARROWS[1].y2}
          stroke="url(#grad-green)"
          strokeWidth="2"
          strokeDasharray="9 5"
          strokeDashoffset={arrowDash}
          markerEnd="url(#arrow-green)"
        />

        {/* Arrow 4: Compiler → Dashboard Badge card */}
        <line
          x1={OUTPUT_ARROWS[2].x1}
          y1={OUTPUT_ARROWS[2].y1}
          x2={OUTPUT_ARROWS[2].x2}
          y2={OUTPUT_ARROWS[2].y2}
          stroke="url(#grad-orange)"
          strokeWidth="2"
          strokeDasharray="9 5"
          strokeDashoffset={arrowDash}
          markerEnd="url(#arrow-orange)"
        />

        {/* ── Flow particles along output arrows ────── */}
        {FLOW_PARTICLES.map((p, i) => {
          const arrow = OUTPUT_ARROWS[p.arrowIndex];
          const progress = ((frame * 0.015 + p.offset) % 1 + 1) % 1;
          const px = lerp(arrow.x1, arrow.x2, progress);
          const py = lerp(arrow.y1, arrow.y2, progress);
          // Fade at edges of journey
          const fadeDist = 0.12;
          const particleOpacity =
            progress < fadeDist
              ? progress / fadeDist
              : progress > 1 - fadeDist
              ? (1 - progress) / fadeDist
              : 1;

          return (
            <circle
              key={i}
              cx={px}
              cy={py}
              r={5}
              fill={p.color}
              opacity={particleOpacity * 0.9 * (compilerOpacity > 0.5 ? 1 : compilerOpacity * 2)}
              style={{ filter: `drop-shadow(0 0 6px ${p.color})` }}
            />
          );
        })}
      </svg>

      {/* ── Bottom ticker row ─────────────────────────── */}
      <div
        style={{
          position: "absolute",
          bottom: 30,
          left: 0,
          right: 0,
          height: 52,
          overflow: "hidden",
        }}
      >
        {/* Left fade */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: 120,
            height: "100%",
            background: `linear-gradient(90deg, ${COLORS.bg}, transparent)`,
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
        {/* Right fade */}
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            width: 120,
            height: "100%",
            background: `linear-gradient(270deg, ${COLORS.bg}, transparent)`,
            zIndex: 2,
            pointerEvents: "none",
          }}
        />

        {/* Scrolling pills — doubled for seamless loop */}
        <div
          style={{
            display: "flex",
            gap: GAP,
            position: "absolute",
            top: 0,
            left: 0,
            transform: `translateX(${tickerOffset}px)`,
            whiteSpace: "nowrap",
          }}
        >
          {[...TICKER_TOOLS, ...TICKER_TOOLS].map((tool, i) => (
            <div
              key={i}
              style={{
                padding: "10px 20px",
                borderRadius: 24,
                background: `${tool.color}18`,
                border: `1.5px solid ${tool.color}60`,
                fontFamily: FONTS.body,
                fontSize: 13,
                color: tool.color,
                whiteSpace: "nowrap",
                boxShadow: `0 0 10px ${tool.color}25`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: PILL_W,
                boxSizing: "border-box",
                flexShrink: 0,
              } as React.CSSProperties}
            >
              {tool.text}
            </div>
          ))}
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
